import { Component, OnInit } from '@angular/core';
import { AuctionType, BankAccountType, AnnualAccountingReportFilter } from '../API.service';
import {
  AnnualAccountingReport,
  AnnualAccountingReportModelService,
  AnnualAccountingReportRow
} from '../model/annual-accounting-report.model';
import {
  auctionDetailPath,
  formatAuctionDate,
  formatTotalAmount
} from './annual-accounting-report.format';

@Component({
  selector: 'app-annual-accounting-report',
  templateUrl: './annual-accounting-report.component.html',
  styleUrls: ['./annual-accounting-report.component.scss']
})
export class AnnualAccountingReportComponent implements OnInit {
  startDate = '';
  endDate = '';
  bankAccountType: BankAccountType | null = null;
  auctionTypes: AuctionType[] = [];
  processing = false;
  errorMsg = '';
  report: AnnualAccountingReport | null = null;

  readonly bankAccountTypes = Object.keys(BankAccountType).map(key => BankAccountType[key as keyof typeof BankAccountType]);
  readonly auctionTypeOptions = Object.keys(AuctionType).map(key => AuctionType[key as keyof typeof AuctionType]);

  constructor(private reportClient: AnnualAccountingReportModelService) { }

  ngOnInit(): void { }

  hasValidDateRange(): boolean {
    return this.isValidDate(this.startDate)
      && this.isValidDate(this.endDate)
      && this.startDate <= this.endDate;
  }

  canExport(): boolean {
    return this.hasValidDateRange() && !this.processing && this.report != undefined;
  }

  async generateReport(): Promise<void> {
    if (!this.hasValidDateRange()) {
      this.errorMsg = 'Enter a valid start and end date.';
      return;
    }

    this.processing = true;
    this.errorMsg = '';
    this.report = null;
    try {
      const filter: AnnualAccountingReportFilter = {
        startDate: this.startDate,
        endDate: this.endDate,
        bankAccountType: this.bankAccountType,
        auctionTypes: this.auctionTypes
      };
      this.report = await this.reportClient.loadReport(filter);
    } catch (err) {
      this.errorMsg = 'Unable to generate the annual accounting report.';
    } finally {
      this.processing = false;
    }
  }

  formatAmount(amount: number | null): string {
    return (amount || 0).toFixed(2);
  }

  formatTotalAmount(amount: number | null): string {
    return formatTotalAmount(amount);
  }

  formatAuctionDate(value: string | null): string {
    return formatAuctionDate(value);
  }

  auctionDetailLink(auctionId: string | null): string {
    return auctionDetailPath(auctionId);
  }

  formatCount(count: number | null): string {
    return String(count || 0);
  }

  toCsv(): string {
    const header = [
      'Auction ID', 'Auction Date', 'Auction Title', 'Auction Type', 'Bank Account',
      'Lots', 'Sold Lots',
      'Gross Sales', 'AR Outstanding', 'Selling Expenses', 'Auction Payables', 'Sales Mgmt. Commission'
    ];
    const rows = this.report ? this.report.rows.map(row => this.rowToCsv(row)) : [];
    const totals = this.report ? [
      'Totals', '', '', '', '',
      this.formatCount(this.report.totals.lotCount),
      this.formatCount(this.report.totals.soldLotCount),
      this.formatAmount(this.report.totals.grossSales),
      this.formatAmount(this.report.totals.accountsReceivableOutstanding),
      this.formatAmount(this.report.totals.sellingExpenses),
      this.formatAmount(this.report.totals.auctionPayables),
      this.formatAmount(this.report.totals.salesManagementCommission)
    ] : [];
    return [header, ...rows, totals].filter(row => row.length > 0).map(row => row.map(this.escapeCsv).join(',')).join('\r\n');
  }

  exportCsv(): void {
    if (!this.canExport()) {
      return;
    }
    const blob = new Blob([this.toCsv()], { type: 'text/csv;charset=utf-8' });
    const link = document.createElement('a');
    const url = window.URL.createObjectURL(blob);
    link.href = url;
    link.download = 'annual-accounting-report-' + this.startDate + '-to-' + this.endDate + '.csv';
    link.click();
    window.URL.revokeObjectURL(url);
  }

  private rowToCsv(row: AnnualAccountingReportRow): string[] {
    return [
      row.auctionId || '', this.formatAuctionDate(row.auctionStartDate), row.title || '', row.auctionType || '', row.bankAccountType || '',
      this.formatCount(row.lotCount), this.formatCount(row.soldLotCount),
      this.formatAmount(row.grossSales), this.formatAmount(row.accountsReceivableOutstanding),
      this.formatAmount(row.sellingExpenses), this.formatAmount(row.auctionPayables),
      this.formatAmount(row.salesManagementCommission)
    ];
  }

  private escapeCsv(value: string): string {
    if (/^[=+\-@]/.test(value)) {
      value = "'" + value;
    }
    return /[",\r\n]/.test(value) ? '"' + value.replace(/"/g, '""') + '"' : value;
  }

  private isValidDate(value: string): boolean {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) {
      return false;
    }
    const parts = value.split('-').map(Number);
    const date = new Date(Date.UTC(parts[0], parts[1] - 1, parts[2]));
    return date.getUTCFullYear() === parts[0]
      && date.getUTCMonth() === parts[1] - 1
      && date.getUTCDate() === parts[2];
  }
}
